import { useEffect, useState, type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';

/**
 * Small shared UI bits reused across the vertical-slice pages, kept
 * dependency-free (no CSS framework — none can be installed in this build
 * sandbox anyway) so every page's loading/error/status presentation is
 * consistent without copy-pasting the same three lines of markup everywhere.
 *
 * VISUAL SYSTEM (2026-08-16, reconciliation pass): retrofitted from the
 * pre-design-system ad hoc hex palette (#82817c/#d03b3b/#1a7f37/etc.,
 * predating `src/styles/tokens.css`) to the centralized `--alex-*` tokens,
 * matching AppShell.tsx/Login.tsx. Before this pass, the app shell (sidebar/
 * topbar) was on-brand but every page's actual content — tables, badges,
 * loading/error text — still rendered in the old, different palette. Status
 * semantics (DRAFT/CANCELLED/INACTIVE = muted, SUBMITTED/ISSUED/
 * PARTIALLY_RECEIVED = warning, APPROVED/RECEIVED/POSTED/ACTIVE = success,
 * REJECTED = danger, CONVERTED = primary) are unchanged — only the
 * underlying color values now come from tokens.css, per the owner's "no
 * component may hardcode a hex value" rule. STATUS: IMPLEMENTED_UNVERIFIED
 * — see docs/data-dictionary.md §8k.
 */

export function LoadingNote({ text = 'Memuat...' }: { text?: string }) {
  return <p style={{ color: 'var(--alex-text-muted)', fontSize: 'var(--alex-fs-body)' }}>{text}</p>;
}

export function ErrorNote({ text }: { text: string }) {
  return (
    <p
      style={{
        color: 'var(--alex-danger)',
        fontSize: 'var(--alex-fs-body)',
        background: 'var(--alex-danger-soft)',
        padding: '8px 10px',
        borderRadius: 'var(--alex-control-radius)',
      }}
      role="alert"
    >
      {text}
    </p>
  );
}

/**
 * Calmer than ErrorNote — for "prerequisite not met" states (no eligible
 * PR to convert, no eligible PO to receive) that are normal application
 * states, not errors. Distinct visual weight so a user never confuses "you
 * did something wrong" with "there's simply nothing to act on yet".
 */
export function HintNote({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        color: 'var(--alex-text-secondary)',
        fontSize: 'var(--alex-fs-caption)',
        background: 'var(--alex-surface-subtle)',
        border: '1px solid var(--alex-border-soft)',
        padding: '8px 12px',
        borderRadius: 'var(--alex-control-radius)',
        marginBottom: 12,
      }}
    >
      <span>{text}</span>
      {action}
    </div>
  );
}

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  DRAFT: { bg: 'var(--alex-border-soft)', fg: 'var(--alex-text-secondary)' },
  SUBMITTED: { bg: 'var(--alex-warning-soft)', fg: '#92620A' },
  APPROVED: { bg: 'var(--alex-success-soft)', fg: 'var(--alex-success)' },
  REJECTED: { bg: 'var(--alex-danger-soft)', fg: 'var(--alex-danger)' },
  CONVERTED: { bg: 'var(--alex-primary-soft)', fg: 'var(--alex-primary)' },
  CANCELLED: { bg: 'var(--alex-border-soft)', fg: 'var(--alex-text-secondary)' },
  ISSUED: { bg: 'var(--alex-warning-soft)', fg: '#92620A' },
  PARTIALLY_RECEIVED: { bg: 'var(--alex-warning-soft)', fg: '#92620A' },
  RECEIVED: { bg: 'var(--alex-success-soft)', fg: 'var(--alex-success)' },
  POSTED: { bg: 'var(--alex-success-soft)', fg: 'var(--alex-success)' },
  ACTIVE: { bg: 'var(--alex-success-soft)', fg: 'var(--alex-success)' },
  INACTIVE: { bg: 'var(--alex-border-soft)', fg: 'var(--alex-text-secondary)' },
  TERHUBUNG: { bg: 'var(--alex-success-soft)', fg: 'var(--alex-success)' },
  'BELUM TERHUBUNG': { bg: 'var(--alex-border-soft)', fg: 'var(--alex-text-faint)' },
  // FASE 12 (migration 0050): report_runs.status.
  FINAL: { bg: 'var(--alex-success-soft)', fg: 'var(--alex-success)' },
  PRELIMINARY: { bg: 'var(--alex-warning-soft)', fg: '#92620A' },
  FAILED: { bg: 'var(--alex-danger-soft)', fg: 'var(--alex-danger)' },
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? { bg: 'var(--alex-border-soft)', fg: 'var(--alex-text-secondary)' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 'var(--alex-fs-micro)',
        fontWeight: 700,
        letterSpacing: 0.2,
        color: tone.fg,
        background: tone.bg,
      }}
    >
      {status}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        <h1 style={{ fontSize: 'var(--alex-fs-page-title)', fontWeight: 'var(--alex-fw-page-title)' as unknown as number, margin: 0, color: 'var(--alex-text-primary)', letterSpacing: -0.4 }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ color: 'var(--alex-text-muted)', fontSize: 13, margin: '4px 0 0', maxWidth: 680 }}>{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable form/action controls — every page in the app renders these
// instead of raw <button>/<input>/<select>, so control styling (radius,
// border, focus state, disabled state) is defined once against the design
// tokens rather than per-page.
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_VARIANT_STYLE: Record<ButtonVariant, CSSProperties> = {
  primary: { background: 'var(--alex-primary)', color: '#fff', border: '1px solid var(--alex-primary)' },
  secondary: { background: 'var(--alex-surface)', color: 'var(--alex-text-secondary)', border: '1px solid var(--alex-border)' },
  danger: { background: 'var(--alex-danger)', color: '#fff', border: '1px solid var(--alex-danger)' },
  ghost: { background: 'transparent', color: 'var(--alex-text-secondary)', border: '1px solid transparent' },
};

export function Button({
  variant = 'secondary',
  loading,
  children,
  style,
  disabled,
  ...rest
}: { variant?: ButtonVariant; loading?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      style={{
        ...BUTTON_VARIANT_STYLE[variant],
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'var(--alex-control-height)',
        padding: '0 13px',
        borderRadius: 'var(--alex-control-radius)',
        fontSize: 'var(--alex-fs-caption)',
        fontWeight: 600,
        cursor: disabled || loading ? 'default' : 'pointer',
        opacity: disabled || loading ? 0.55 : 1,
        transition: 'background 120ms ease, opacity 120ms ease, border-color 120ms ease',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {loading ? 'Memuat...' : children}
    </button>
  );
}

const fieldBaseStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: 'var(--alex-control-height)',
  padding: '0 10px',
  border: '1px solid var(--alex-border)',
  borderRadius: 'var(--alex-control-radius)',
  fontSize: 'var(--alex-fs-caption)',
  color: 'var(--alex-text-primary)',
  background: 'var(--alex-surface)',
  fontFamily: 'var(--alex-font-family)',
};

export function FieldLabel({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-secondary)', fontWeight: 600, display: 'block' }}>
      {label}
      <div style={{ marginTop: 3 }}>{children}</div>
    </label>
  );
}

export function TextInput({ style, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} style={{ ...fieldBaseStyle, ...style }} />;
}

export function Select({ style, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} style={{ ...fieldBaseStyle, ...style }}>
      {children}
    </select>
  );
}

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="alex-card" style={{ padding: 'var(--alex-card-padding)', ...style }}>
      {children}
    </div>
  );
}

type Tone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  primary: { bg: 'var(--alex-primary-soft)', fg: 'var(--alex-primary)' },
  success: { bg: 'var(--alex-success-soft)', fg: 'var(--alex-success)' },
  warning: { bg: 'var(--alex-warning-soft)', fg: '#92620A' },
  danger: { bg: 'var(--alex-danger-soft)', fg: 'var(--alex-danger)' },
  info: { bg: 'var(--alex-info-soft)', fg: '#0B6E96' },
  neutral: { bg: 'var(--alex-border-soft)', fg: 'var(--alex-text-secondary)' },
};

export function IconCircle({ icon, tone = 'primary', size = 32 }: { icon: ReactNode; tone?: Tone; size?: number }) {
  const t = TONE_STYLE[tone];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: t.bg,
        color: t.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
  );
}

export function KPICard({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
  delta,
  linkTo,
  linkLabel,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  icon?: ReactNode;
  tone?: Tone;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  linkTo?: string;
  linkLabel?: string;
  /** Opens a detail/preview (drawer, filtered view, etc). Renders the whole
   * card as a real button — mutually exclusive with linkTo (a card either
   * navigates via its own click or exposes a separate "Lihat semua" link;
   * never both, to avoid a link nested inside a clickable container). */
  onClick?: () => void;
}) {
  const deltaColor = delta?.direction === 'up' ? 'var(--alex-success)' : delta?.direction === 'down' ? 'var(--alex-danger)' : 'var(--alex-text-muted)';
  const deltaGlyph = delta?.direction === 'up' ? '▲' : delta?.direction === 'down' ? '▼' : '•';
  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {icon && <IconCircle icon={icon} tone={tone} size={28} />}
          <div style={{ fontSize: 12, color: 'var(--alex-text-muted)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
        </div>
        {delta && (
          <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <span aria-hidden>{deltaGlyph}</span>
            {delta.value}
          </span>
        )}
      </div>
      <div style={{ fontSize: 'var(--alex-fs-kpi-value)', fontWeight: 'var(--alex-fw-kpi-value)' as unknown as number, color: 'var(--alex-text-primary)', letterSpacing: -0.3 }}>
        {value}
      </div>
      {(hint || linkTo || onClick) && (
        <div style={{ fontSize: 11, color: 'var(--alex-text-faint)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
          {hint && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hint}</span>}
          {linkTo && !onClick && (
            <Link to={linkTo} style={{ color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
              {linkLabel ?? 'Detail'} →
            </Link>
          )}
          {onClick && <span style={{ color: 'var(--alex-primary)', fontWeight: 600, flexShrink: 0 }}>{linkLabel ?? 'Detail'} →</span>}
        </div>
      )}
    </>
  );
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="alex-card alex-kpi-clickable"
        style={{ padding: 'var(--alex-kpi-padding)', minWidth: 168, flex: '1 1 168px', display: 'flex', flexDirection: 'column', gap: 7, textAlign: 'left', cursor: 'pointer', border: 'none', font: 'inherit' }}
      >
        {body}
      </button>
    );
  }
  return (
    <div className="alex-card" style={{ padding: 'var(--alex-kpi-padding)', minWidth: 168, flex: '1 1 168px', display: 'flex', flexDirection: 'column', gap: 7 }}>
      {body}
    </div>
  );
}

export function SectionCard({
  title,
  action,
  actionLabel = 'Lihat semua',
  children,
  style,
}: {
  title: string;
  action?: string;
  actionLabel?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div className="alex-card" style={{ padding: 'var(--alex-card-padding)', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 'var(--alex-fs-section-title)', fontWeight: 'var(--alex-fw-section-title)' as unknown as number, color: 'var(--alex-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.25 }}>
          {title}
        </h3>
        {action && (
          <Link to={action} style={{ color: 'var(--alex-primary)', fontSize: 10.5, fontWeight: 700, textDecoration: 'none' }}>
            {actionLabel} →
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Use in place of a "Lihat semua"/action link when the target module isn't
 * built yet. Renders as visibly non-interactive (muted, no hover, default
 * cursor) with a native title tooltip explaining why — the strict rule
 * this app follows is that a control either truly navigates somewhere or
 * is unmistakably inert, never a link that looks live and goes nowhere.
 */
export function ComingSoonTag({ label = 'Segera hadir' }: { label?: string }) {
  return (
    <span
      title="Modul ini belum tersedia di build ini"
      style={{
        fontSize: 'var(--alex-fs-micro)',
        fontWeight: 600,
        color: 'var(--alex-text-faint)',
        cursor: 'default',
        userSelect: 'none',
      }}
    >
      {label}
    </span>
  );
}

export function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--alex-fs-sidebar-group)',
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: 'var(--alex-sidebar-text-muted)',
        padding: '10px 9px 3px',
        opacity: 0.75,
      }}
    >
      {children}
    </div>
  );
}

export function DemoBanner({ text }: { text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        color: 'var(--alex-text-faint)',
        padding: '0 2px 10px',
        fontSize: 10.5,
        fontWeight: 500,
        lineHeight: 1.4,
      }}
    >
      <span aria-hidden style={{ opacity: 0.7 }}>ⓘ</span>
      {text}
    </div>
  );
}

export function Skeleton({ width = '100%', height = 16, style }: { width?: number | string; height?: number | string; style?: CSSProperties }) {
  return <div className="alex-skeleton" style={{ width, height, ...style }} />;
}

export function DashboardSkeleton() {
  return (
    <div>
      <div style={{ display: 'flex', gap: 'var(--alex-card-gap)', marginBottom: 12, flexWrap: 'wrap' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="alex-card" style={{ padding: 'var(--alex-kpi-padding)', flex: '1 1 148px', minWidth: 148 }}>
            <Skeleton width={24} height={24} style={{ borderRadius: '50%', marginBottom: 8 }} />
            <Skeleton width="60%" height={9} style={{ marginBottom: 6 }} />
            <Skeleton width="40%" height={16} />
          </div>
        ))}
      </div>
      <div className="alex-card" style={{ padding: 'var(--alex-card-padding)' }}>
        <Skeleton width="30%" height={12} style={{ marginBottom: 10 }} />
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={28} style={{ marginBottom: 5 }} />
        ))}
      </div>
    </div>
  );
}

/** Tiny inline SVG line/area chart — no charting library dependency. */
export function Sparkline({
  points,
  labels,
  color = 'var(--alex-primary)',
  height = 64,
}: {
  points: number[];
  labels?: string[];
  color?: string;
  height?: number;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  if (points.length < 2) return null;
  const width = 260;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const stepX = width / (points.length - 1);
  const coords = points.map((p, i) => [i * stepX, height - ((p - min) / range) * (height - 8) - 4]);
  const linePath = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;
  const gradId = `alex-spark-${Math.round(max * 97 + min * 13)}`;
  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.22} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {coords.map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={selected === i ? 5 : 9}
            fill={selected === i ? color : 'transparent'}
            stroke="none"
            style={{ cursor: 'pointer' }}
            onClick={() => setSelected(selected === i ? null : i)}
          >
            <title>{`${labels?.[i] ?? `#${i + 1}`}: ${points[i]}`}</title>
          </circle>
        ))}
      </svg>
      {selected !== null && (
        <div style={{ marginTop: 6, fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-primary)' }}>
          <b>{labels?.[selected] ?? `Titik #${selected + 1}`}</b> <span style={{ color: 'var(--alex-text-muted)' }}>· {points[selected]}</span>
        </div>
      )}
    </div>
  );
}

interface DonutSegment {
  value: number;
  color: string;
  label: string;
}

/**
 * Inline SVG donut chart — no charting library dependency. Real
 * interaction, not decoration: hover (desktop) shows a native tooltip via
 * <title>; click/tap (any device) selects the segment and shows its
 * category/value/percentage in a readout below the chart, which is how
 * this also works on mobile where hover doesn't exist.
 */
export function DonutChart({
  segments,
  size = 120,
  thickness = 16,
  selected: controlledSelected,
  onSelectedChange,
}: {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  /** Pass both to let a custom legend outside the chart drive/read the same
   * selection state (e.g. clicking a legend row highlights the matching
   * segment). Omit either to fall back to the chart's own internal state. */
  selected?: number | null;
  onSelectedChange?: (index: number | null) => void;
}) {
  const [internalSelected, setInternalSelected] = useState<number | null>(null);
  const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;
  const setSelected = onSelectedChange ?? setInternalSelected;
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const active = selected !== null ? segments[selected] : null;
  return (
    <div>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--alex-border-soft)" strokeWidth={thickness} />
        {segments.map((seg, i) => {
          const fraction = seg.value / total;
          const dash = fraction * circumference;
          const isSelected = selected === i;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={isSelected ? thickness + 3 : thickness}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              strokeLinecap="butt"
              style={{ cursor: 'pointer', opacity: selected === null || isSelected ? 1 : 0.45, transition: 'opacity 120ms ease, stroke-width 120ms ease' }}
              onClick={() => setSelected(isSelected ? null : i)}
            >
              <title>{`${seg.label}: ${seg.value} (${((fraction) * 100).toFixed(1)}%)`}</title>
            </circle>
          );
          offset += dash;
          return el;
        })}
      </svg>
      {active && (
        <div style={{ marginTop: 8, fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: active.color, display: 'inline-block' }} />
          <b>{active.label}</b>
          <span style={{ color: 'var(--alex-text-muted)' }}>
            · {active.value} ({((active.value / total) * 100).toFixed(1)}%)
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Slide-over detail panel — used for real read-only row drilldowns (PO/GR
 * detail) instead of making a document number a "looks clickable, does
 * nothing" element. Closes on Escape, backdrop click, or the close button;
 * autofocuses the close button so keyboard users land somewhere sane.
 */
export function Drawer({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="alex-card"
        style={{
          width: 460,
          maxWidth: '92vw',
          height: '100%',
          borderRadius: 0,
          borderLeft: '1px solid var(--alex-border)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--alex-shadow-popover)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--alex-border-soft)' }}>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--alex-text-primary)' }}>{title}</h3>
          <button
            autoFocus
            onClick={onClose}
            aria-label="Tutup"
            style={{ border: 'none', background: 'none', fontSize: 16, color: 'var(--alex-text-faint)', cursor: 'pointer', lineHeight: 1, padding: 4 }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

export function DrawerField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-primary)', fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}

type EmptyStateVariant = 'compact' | 'table' | 'section';

const EMPTY_STATE_PADDING: Record<EmptyStateVariant, string> = {
  compact: '16px 14px',
  table: '26px 14px',
  section: '36px 18px',
};

/**
 * Small-by-default empty state — 'compact' (inline in a KPI/list row),
 * 'table' (in place of a table body), 'section' (a whole SectionCard with
 * nothing to show). None of these fill a whole card with dead space; the
 * largest variant tops out around 70-90px, not a few hundred.
 */
export function EmptyState({ text, variant = 'table' }: { text: string; variant?: EmptyStateVariant }) {
  return (
    <div
      style={{
        padding: EMPTY_STATE_PADDING[variant],
        textAlign: 'center',
        color: 'var(--alex-text-faint)',
        fontSize: variant === 'compact' ? 11.5 : 'var(--alex-fs-caption)',
        background: 'var(--alex-surface-subtle)',
        borderRadius: 'var(--alex-control-radius)',
      }}
    >
      {text}
    </div>
  );
}

export const tableClassName = 'alex-table';
export const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--alex-fs-table)' };
export const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '9px 12px',
  background: 'var(--alex-surface-subtle)',
  borderBottom: '1px solid var(--alex-border)',
  color: 'var(--alex-text-muted)',
  fontWeight: 700,
  fontSize: 11.5,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};
export const tdStyle: CSSProperties = { padding: '0 12px', height: 'var(--alex-row-height)', boxSizing: 'border-box', borderBottom: '1px solid var(--alex-border-soft)', color: 'var(--alex-text-primary)' };

/**
 * A real, keyboard-accessible clickable <tr> — used for any table row that
 * opens a drawer or navigates, instead of a row that merely looks
 * inspectable (blue text/cursor-pointer) but does nothing on click. Never
 * use blue "looks clickable" text on a row unless it's wrapped in this.
 */
export function ClickableRow({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <tr
      className="alex-row-clickable"
      onClick={onClick}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {children}
    </tr>
  );
}

/**
 * Content block for a Drawer opened from an illustrative/DEMO widget
 * (Platform Owner, Finance, HR previews with no live backend yet).
 * ILLUSTRATIVE data must stay interactive per the app's UX rules, but the
 * drawer must never let a user believe the number is live — this always
 * opens with an explicit "data ilustrasi" banner, never bare numbers.
 */
export function IllustrativePreview({
  description,
  breakdown,
}: {
  description: string;
  breakdown?: { label: string; value: ReactNode }[];
}) {
  return (
    <div>
      <DemoBanner text="Data ilustrasi — belum terhubung ke backend nyata. Ini adalah pratinjau tampilan/perilaku modul, bukan data produksi." />
      <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', lineHeight: 1.5 }}>{description}</p>
      {breakdown && breakdown.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {breakdown.map((row, i) => (
            <DrawerField key={i} label={row.label} value={row.value} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enterprise list-workspace kit — shared by every operational list page
// (Items/Suppliers/PR/PO/GR/Stock Ledger/Stock Balance) so they read as one
// product family instead of "title + button + naked table". Deliberately
// simple client-side search/filter/sort/pagination over an already-fetched
// real array — none of these pages have a paginated backend endpoint, and
// the data volumes in this vertical slice don't need one. No fabricated
// data anywhere in this kit; it only reshapes what the page already loaded.
// ---------------------------------------------------------------------------

/** Safe date/time formatter — never renders "Invalid Date". Returns '—' for
 * null/undefined/unparseable input instead of trusting every caller to
 * guard it themselves. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('id-ID');
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('id-ID');
}

/** Detects internal QA/fixture text living in a genuine business field
 * (item/supplier name, PR/waste notes, etc.) — e.g. "DEV Item 01" or
 * "DEV FIXTURE — expiry lot closure test". These are real, intentionally
 * seeded records in the alex-erp-dev environment (this project's only
 * environment), not a bug to delete or a historical record to rewrite —
 * some of them belong to CONVERTED/POSTED documents, so editing the
 * stored text would alter evidence. Detecting the pattern lets the UI
 * flag it clearly as DEV-only test content instead of presenting it as
 * ordinary business text, without ever touching the underlying value. */
const DEV_FIXTURE_PATTERN = /\bDEV\b|\bFIXTURE\b/i;
export function isDevFixtureText(text: string | null | undefined): boolean {
  return !!text && DEV_FIXTURE_PATTERN.test(text);
}

/** Renders a business-text field (name/notes/etc.) as-is, or — when it
 * matches isDevFixtureText — with a small "DEV" tag so a reviewer can tell
 * at a glance this is seeded test content, not a real business record.
 * The displayed text itself is never altered. */
export function BusinessText({ text, fallback = '—' }: { text: string | null | undefined; fallback?: string }) {
  if (!text) return <>{fallback}</>;
  if (!isDevFixtureText(text)) return <>{text}</>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span>{text}</span>
      <span
        title="Data fixture/pengujian internal (alex-erp-dev) — bukan data bisnis nyata"
        style={{
          fontSize: 9,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          padding: '1px 6px',
          borderRadius: 999,
          background: 'var(--alex-warning-soft)',
          color: '#92620A',
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        Dev fixture
      </span>
    </span>
  );
}

/** Counts rows per status against the FULL canonical status list for that
 * record's type (e.g. every value of PurchaseRequestStatus) — every key is
 * always present, defaulting to 0, so a summary strip built from this can
 * never silently omit a real status bucket the way a hand-picked subset of
 * `.filter()` calls can. The canonical list should always be spelled out
 * as the type's exact literal union so a compile error requires callers to
 * account for a bucket if the type gains one. */
export function statusCounts<S extends string>(rows: { status: S }[], statuses: readonly S[]): Record<S, number> {
  const counts = Object.fromEntries(statuses.map((s) => [s, 0])) as Record<S, number>;
  for (const row of rows) {
    if (row.status in counts) counts[row.status] += 1;
  }
  return counts;
}

/** Compact stat chip row for the top of a list page — smaller/denser than
 * KPICard (no icon circle, no link), reads as a real-time summary strip
 * rather than a dashboard. Every value passed in must be a real count. */
export function SummaryStrip({ items }: { items: { label: string; value: ReactNode; tone?: Tone }[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
      {items.map((it, i) => (
        <div
          key={i}
          className="alex-card"
          style={{ padding: '9px 14px', display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}
        >
          <span style={{ fontSize: 17, fontWeight: 700, color: it.tone ? TONE_STYLE[it.tone].fg : 'var(--alex-text-primary)' }}>{it.value}</span>
          <span style={{ fontSize: 11, color: 'var(--alex-text-muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Toolbar row: search input + arbitrary filter controls + a real result
 * count, right-aligned. Wraps on narrow viewports instead of overflowing. */
export function ListToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Cari...',
  filters,
  resultCount,
  resultLabel = 'hasil',
}: {
  search?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  filters?: ReactNode;
  resultCount?: number;
  resultLabel?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
      {onSearchChange && (
        <div style={{ position: 'relative', width: 240, maxWidth: '100%' }}>
          <TextInput value={search ?? ''} onChange={(e) => onSearchChange(e.target.value)} placeholder={searchPlaceholder} style={{ paddingLeft: 30 }} />
          <span aria-hidden style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--alex-text-faint)', fontSize: 13 }}>
            ⌕
          </span>
        </div>
      )}
      {filters}
      {resultCount !== undefined && (
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--alex-text-faint)', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {resultCount} {resultLabel}
        </span>
      )}
    </div>
  );
}

/** Client-side pagination over an already-fetched real array. Returns the
 * current page slice plus a <Pagination/> control — every page's list
 * table wires both from one hook so paging logic isn't reimplemented per
 * page. Resets to page 1 whenever the input row count changes (e.g. a new
 * search/filter narrows the set). */
export function usePagination<T>(rows: T[], pageSize = 15) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);
  useEffect(() => {
    setPage(1);
  }, [rows.length]);
  return { pageRows, page: safePage, pageCount, setPage, start, pageSize, total: rows.length };
}

export function Pagination({ page, pageCount, setPage, start, pageSize, total }: { page: number; pageCount: number; setPage: (p: number) => void; start: number; pageSize: number; total: number }) {
  if (total === 0) return null;
  const end = Math.min(start + pageSize, total);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11.5, color: 'var(--alex-text-faint)' }}>
        Menampilkan {start + 1}–{end} dari {total}
      </span>
      {pageCount > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Button variant="secondary" onClick={() => setPage(page - 1)} disabled={page <= 1} style={{ padding: '0 10px' }}>
            ← Sebelumnya
          </Button>
          <span style={{ fontSize: 11.5, color: 'var(--alex-text-muted)', fontWeight: 600 }}>
            Hal {page} / {pageCount}
          </span>
          <Button variant="secondary" onClick={() => setPage(page + 1)} disabled={page >= pageCount} style={{ padding: '0 10px' }}>
            Berikutnya →
          </Button>
        </div>
      )}
    </div>
  );
}
